import { useEffect, useId, useState } from 'react'
import { fetchPart, fetchTransactionsPage } from '../services/inventoryApi'
import { partTitle } from '../utils/stock'

export default function DeletePartModal({ part, open, onClose, onConfirm, saving = false }) {
  const titleId = useId()
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || !part?.id) return undefined
    setError('')
    setPreview({
      part,
      quantity: Number(part.quantity) || 0,
      transactionCount: null,
    })
    let cancelled = false
    setLoading(true)
    Promise.all([fetchPart(part.id), fetchTransactionsPage({ partId: part.id, page: 1, limit: 1 })])
      .then(([latest, history]) => {
        if (cancelled) return
        const nextPart = latest || part
        setPreview({
          part: nextPart,
          quantity: Number(nextPart.quantity) || 0,
          transactionCount: Number(history.total) || 0,
        })
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || '최신 정보를 확인하지 못했습니다. 삭제 시 서버에서 다시 검사합니다.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, part?.id])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, saving])

  if (!open || !part) return null

  const display = preview?.part || part
  const quantity = preview?.quantity ?? (Number(part.quantity) || 0)
  const unit = display.unit || part.unit || '개'
  const txCount = preview?.transactionCount
  const txLabel = txCount == null ? (loading ? '확인 중...' : '-') : txCount === 0 ? '없음' : `${txCount}건`

  async function handleConfirm() {
    setError('')
    try {
      await onConfirm(display)
    } catch (err) {
      setError(err.message || '품목을 삭제할 수 없습니다.')
    }
  }

  return (
    <div
      className="modal-backdrop modal-backdrop--elevated"
      onClick={(event) => {
        event.stopPropagation()
        if (!saving) onClose()
      }}
      role="presentation"
    >
      <div
        className="modal-panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="modal-title">
          품목 삭제
        </h2>
        <p className="modal-model">{partTitle(display)}</p>
        <p className="muted">{display.partCode || part.partCode}</p>
        <p>이 품목을 삭제하시겠습니까?</p>
        <dl className="delete-preview">
          <div>
            <dt>현재수량</dt>
            <dd>
              {quantity}
              {unit}
            </dd>
          </div>
          <div>
            <dt>거래이력</dt>
            <dd>{txLabel}</dd>
          </div>
        </dl>
        <p className="delete-warning">이 작업은 되돌릴 수 없습니다.</p>
        {loading ? <p className="muted">서버에서 삭제 조건을 확인하는 중...</p> : null}
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button
            type="button"
            className="btn btn--danger btn--touch"
            onClick={handleConfirm}
            disabled={saving || loading}
          >
            {saving ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    </div>
  )
}
